import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { InputText } from "../../../components/InputText";
import { Label } from "../../../components/ui/label";
import { SiglaModal } from "../../../components/modals";
import {
  Plus,
  Edit,
  Search,
  FileText,
  Hash,
  CheckCircle,
  XCircle,
  Loader2,
  TrendingUp,
  AlertTriangle,
  Shield
} from "lucide-react";
import { api } from "../../../services/api";

interface Sigla {
  id: string;
  sigla: string;
  descricao: string;
  id_habbo_variavel?: number | null;
  status: string;
  criado_em: string;
  alterado_em: string;
}



export function Siglas() {
  const [siglas, setSiglas] = useState<Sigla[]>([]);
  const [filteredSiglas, setFilteredSiglas] = useState<Sigla[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSigla, setEditingSigla] = useState<Sigla | null>(null);

  useEffect(() => {
    loadSiglas();
  }, []);



  const loadSiglas = async () => {
    try {
      setLoading(true);
      const response = await api.get("/siglas");
      // Ordenar por sigla
      const sortedSiglas = response.data.sort((a: Sigla, b: Sigla) => a.sigla.localeCompare(b.sigla));
      setSiglas(sortedSiglas);
      setFilteredSiglas(sortedSiglas);
    } catch (error) {
      enqueueSnackbar("Erro ao carregar siglas", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Filtrar siglas baseado na busca
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchTerm.trim()) {
      setFilteredSiglas(siglas);
    } else {
      const filtered = siglas.filter(
        (sigla) =>
          sigla.sigla.toLowerCase().includes(searchTerm.toLowerCase()) ||
          sigla.descricao.toLowerCase().includes(searchTerm.toLowerCase())
      );
      // Manter ordenação após filtro
      const sortedFiltered = filtered.sort((a, b) => a.sigla.localeCompare(b.sigla));
      setFilteredSiglas(sortedFiltered);
    }
  };

  // Atualizar filtro quando o termo de busca mudar
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredSiglas(siglas);
    } else {
      const filtered = siglas.filter(
        (sigla) =>
          sigla.sigla.toLowerCase().includes(searchTerm.toLowerCase()) ||
          sigla.descricao.toLowerCase().includes(searchTerm.toLowerCase())
      );
      // Manter ordenação após filtro
      const sortedFiltered = filtered.sort((a, b) => a.sigla.localeCompare(b.sigla));
      setFilteredSiglas(sortedFiltered);
    }
  }, [searchTerm, siglas]);





  const handleEdit = (sigla: Sigla) => {
    setEditingSigla(sigla);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSigla(null);
  };

  const handleSaveSuccess = () => {
    loadSiglas();
    handleCloseModal();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-border pb-4">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <FileText className="h-6 w-6 text-[oklch(0.45_0.15_142)]" />
          Siglas
        </h1>
        <p className="text-muted-foreground mt-2">
          Gerencie as siglas e abreviações do sistema
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conteúdo Principal */}
        <div className="lg:col-span-2 space-y-6">

          {/* Filtros de Busca */}
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)]">
                <Search className="w-5 h-5" />
                Buscar Siglas
              </CardTitle>
              <CardDescription>
                Digite a sigla ou descrição para pesquisar ou deixe em branco para ver todas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor="search">Sigla ou Descrição</Label>
                  <InputText
                    id="search"
                    name="search"
                    title=""
                    placeholder="Digite a sigla ou descrição..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                  >
                    {loading ? "Buscando..." : "Buscar"}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Nova Sigla
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Resultados */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)]">Resultados</CardTitle>
              <CardDescription>
                {filteredSiglas.length} sigla(s) encontrada(s)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-muted-foreground">Carregando...</div>
                </div>
              ) : filteredSiglas.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhuma sigla encontrada
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredSiglas.map((sigla) => (
                    <div
                      key={sigla.id}
                      className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-[oklch(0.45_0.15_142)]/10 rounded-lg flex items-center justify-center">
                          <Hash className="w-5 h-5 text-[oklch(0.45_0.15_142)]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground font-mono">{sigla.sigla}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={
                              sigla.status === "A"
                                ? "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700"
                                : "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800/40 dark:text-gray-300 dark:border-gray-600"
                            }>
                              {sigla.status === "A" ? (
                                <CheckCircle className="h-3 w-3 mr-1" />
                              ) : (
                                <XCircle className="h-3 w-3 mr-1" />
                              )}
                              {sigla.status === "A" ? "Ativo" : "Inativo"}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 max-w-md">
                            {sigla.descricao}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            ID Habbo: <span className="font-medium">{sigla.id_habbo_variavel ?? "N/A"}</span>
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(sigla)}
                          className="text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/20 hover:bg-[oklch(0.45_0.15_142)]/10 dark:text-[oklch(0.75_0.15_142)] dark:border-[oklch(0.45_0.15_142)]/40 dark:hover:bg-[oklch(0.45_0.15_142)]/20"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Editar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Painel Lateral */}
        <div className="space-y-6">
          {/* Estatísticas */}
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)] dark:text-foreground flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Estatísticas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total de Siglas</span>
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700">
                    {siglas.length}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Siglas Ativas</span>
                  <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700">
                    {siglas.filter(s => s.status === "A").length}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Siglas Inativas</span>
                  <Badge className="bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800/40 dark:text-gray-300 dark:border-gray-600">
                    {siglas.filter(s => s.status === "I").length}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Encontradas</span>
                  <Badge className="bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700">
                    {filteredSiglas.length}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Informações */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Dicas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                            <div className="p-3 bg-amber-50 dark:bg-amber-950/40 rounded-lg border border-transparent dark:border-amber-800">
                <div className="flex items-start gap-2">
                  <Shield className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-900 dark:text-amber-300">Padronização</p>
                    <p className="text-amber-700 dark:text-amber-400">
                      Mantenha as siglas em maiúsculas para consistência
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-3 bg-blue-50 dark:bg-blue-950/40 rounded-lg border border-transparent dark:border-blue-800">
                <div className="flex items-start gap-2">
                  <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-900 dark:text-blue-300">Descrição</p>
                    <p className="text-blue-700 dark:text-blue-400">
                      Sempre inclua uma descrição clara e detalhada
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modal */}
      <SiglaModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        editingSigla={editingSigla}
        onSuccess={handleSaveSuccess}
      />
    </div>
  );
}
